// Authentication hook - 83 lines ✅
import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import type { User, Organization, AuthSession } from '@/types/auth'
import type { Session } from '@supabase/supabase-js'

interface UseAuthReturn {
  user: User | null
  organization: Organization | null
  session: AuthSession | null
  isLoading: boolean
  isAuthenticated: boolean
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
  error: Error | null
}

export function useAuth(): UseAuthReturn {
  const [user, setUser] = useState<User | null>(null)
  const [organization, setOrganization] = useState<Organization | null>(null)
  const [session, setSession] = useState<AuthSession | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const navigate = useNavigate()

  const fetchUserData = useCallback(async (userId: string) => {
    try {
      // Fetch user data first
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .single()

      if (userError) throw userError
      if (!userData) throw new Error('User data not found')

      // Map snake_case DB fields to camelCase types
      const mappedUser: User = {
        id: userData.id,
        organizationId: userData.organization_id,
        email: userData.email,
        fullName: userData.full_name,
        role: userData.role as User['role'],
        avatarUrl: userData.avatar_url,
        preferences: (userData.preferences ?? {}) as User['preferences'],
        lastLoginAt: userData.last_login_at,
        isActive: userData.is_active ?? true,
        createdAt: userData.created_at,
        updatedAt: userData.updated_at,
      }
      setUser(mappedUser)

      // Fetch organization separately
      if (userData.organization_id) {
        const { data: orgData, error: orgError } = await supabase
          .from('organizations')
          .select('*')
          .eq('id', userData.organization_id)
          .single()

        if (!orgError && orgData) {
          const mappedOrg: Organization = {
            id: orgData.id,
            name: orgData.name,
            slug: orgData.slug,
            settings: {
              d365Version: orgData.d365_version as string | undefined,
              d365DeploymentType: orgData.d365_deployment_type as string | undefined,
            },
            subscriptionTier: (orgData.subscription_tier as Organization['subscriptionTier']) ?? 'free',
            maxUsers: (orgData.max_users as number) ?? 5,
            maxStorageMb: (orgData.max_storage_mb as number) ?? 1000,
            isActive: (orgData.is_active as boolean) ?? true,
            createdAt: orgData.created_at as string,
            updatedAt: orgData.updated_at as string,
          }
          setOrganization(mappedOrg)
        }
      }
    } catch (err) {
      setError(err as Error)
    }
  }, [])

  useEffect(() => {
    let mounted = true
    let currentUserId: string | null = null

    const handleSession = (authSession: Session | null) => {
      if (!mounted) return
      const uid = authSession?.user?.id ?? null
      if (uid && uid !== currentUserId) {
        currentUserId = uid
        fetchUserData(uid).finally(() => {
          if (mounted) setIsLoading(false)
        })
      } else if (!uid) {
        currentUserId = null
        setUser(null)
        setOrganization(null)
        setSession(null)
        setIsLoading(false)
      }
    }

    supabase.auth.getSession().then(({ data: { session: authSession } }) => {
      handleSession(authSession)
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, authSession) => {
        if (event === 'SIGNED_IN' || event === 'SIGNED_OUT') {
          handleSession(authSession)
        }
      }
    )

    return () => {
      mounted = false
      subscription.unsubscribe()
    }
  }, [fetchUserData])

  const signIn = useCallback(async (email: string, password: string) => {
    try {
      setError(null)
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      })
      if (signInError) throw signInError
      navigate('/dashboard')
    } catch (err) {
      setError(err as Error)
      throw err
    }
  }, [navigate])

  const signOut = useCallback(async () => {
    await supabase.auth.signOut()
    navigate('/login')
  }, [navigate])

  return {
    user,
    organization,
    session,
    isLoading,
    isAuthenticated: !!user,
    signIn,
    signOut,
    error,
  }
}
